import * as React from 'react';
import type { EventContentArg } from '@fullcalendar/core';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { alpha } from '@mui/material/styles';

import { dayjs } from '@/lib/v2/dayjs';

export interface EventContentProps extends EventContentArg {
  event: EventContentArg['event'] & {
    extendedProps: {
      description?: string;
      priority?: 'high' | 'medium' | 'low';
      type?: string;
      color?: string;
    };
  };
}

// Fallback palette for events with no type color
const DEFAULT_COLOR = '#6366F1';

export function EventContent(arg: EventContentProps): React.JSX.Element {
  const { type, color: eventColor } = arg.event.extendedProps;
  const color = eventColor || DEFAULT_COLOR;

  const isAllDay = arg.event.allDay;
  const startTime = arg.event.start ? dayjs(arg.event.start).format('h:mm A') : null;
  const endTime = arg.event.end ? dayjs(arg.event.end).format('h:mm A') : null;

  const durationMin =
    arg.event.start && arg.event.end
      ? dayjs(arg.event.end).diff(dayjs(arg.event.start), 'minute')
      : null;
  const isShort = durationMin !== null && durationMin < 45;

  return (
    <Box
      sx={{
        height: '100%',
        minHeight: isAllDay ? 28 : 30,
        borderRadius: '5px',
        bgcolor: alpha(color, 0.15),
        borderLeft: `3px solid ${color}`,
        overflow: 'hidden',
        cursor: 'pointer',
        transition: 'filter 0.12s, transform 0.12s',
        '&:hover': {
          filter: 'brightness(1.12)',
          transform: 'translateY(-1px)',
        },
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: isShort ? 'row' : 'column',
          alignItems: isShort ? 'center' : 'flex-start',
          gap: isShort ? '6px' : '2px',
          px: '7px',
          py: isAllDay ? '5px' : '4px',
          height: '100%',
        }}
      >
        {/* Time row */}
        {!isAllDay && startTime && (
          <Typography
            sx={{
              color,
              fontWeight: 600,
              fontSize: '10px',
              lineHeight: 1,
              whiteSpace: 'nowrap',
              opacity: 0.9,
              flexShrink: 0,
            }}
          >
            {isShort ? `${startTime} – ${endTime}` : startTime}
          </Typography>
        )}

        {/* All-day label */}
        {isAllDay && (
          <Typography
            sx={{
              color,
              fontWeight: 700,
              fontSize: '10px',
              lineHeight: 1,
              textTransform: 'uppercase',
              letterSpacing: '0.04em',
              opacity: 0.85,
              flexShrink: 0,
            }}
          >
            All day
          </Typography>
        )}

        {/* Title */}
        <Typography
          sx={{
            color: 'var(--mui-palette-text-primary)',
            fontWeight: 600,
            fontSize: isAllDay ? '12px' : '11px',
            lineHeight: 1.25,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            display: '-webkit-box',
            WebkitLineClamp: isShort ? 1 : 2,
            WebkitBoxOrient: 'vertical',
            flex: 1,
            minWidth: 0,
          }}
        >
          {arg.event.title}
        </Typography>

        {/* Type pill — only when tall enough */}
        {type && !isShort && !isAllDay && (
          <Box
            sx={{
              display: 'inline-flex',
              alignSelf: 'flex-start',
              px: '5px',
              py: '1px',
              borderRadius: '3px',
              bgcolor: alpha(color, 0.25),
              mt: '1px',
            }}
          >
            <Typography
              sx={{
                color,
                fontWeight: 700,
                fontSize: '9px',
                lineHeight: 1.4,
                textTransform: 'uppercase',
                letterSpacing: '0.05em',
                whiteSpace: 'nowrap',
              }}
            >
              {type}
            </Typography>
          </Box>
        )}
      </Box>
    </Box>
  );
}
